import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ArrowUpRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <span className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`h-3.5 w-3.5 ${i <= n ? "fill-[#E63946] text-[#E63946]" : "text-[#3a3a3f]"}`}
        strokeWidth={1.5}
      />
    ))}
  </span>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data))
      .catch(() => {});
  }, []);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="bg-[#0D0D0F] px-5 py-24 sm:px-10 sm:py-36"
    >
      <div className="mx-auto max-w-7xl">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          className="mb-8 block font-mono text-[10px] uppercase tracking-[0.35em] text-[#8E8E93] sm:text-xs"
        >
          07 — REVIEWS
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-[clamp(2.6rem,7vw,6.5rem)] font-black uppercase leading-[0.9] tracking-tight text-[#F3F3F5]"
        >
          STRAIGHT FROM <span className="text-[#8E8E93]">THE MESSAGES.</span>
        </motion.h2>

        {reviews.length === 0 ? (
          <motion.div
            data-testid="reviews-empty-state"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="mt-16 flex aspect-[16/6] items-center justify-center border border-white/10"
          >
            <span className="px-4 text-center font-mono text-[10px] uppercase tracking-[0.4em] text-[#3a3a3f] sm:text-xs">
              FIRST REVIEWS LANDING SOON.
            </span>
          </motion.div>
        ) : (
          <div className="mt-16 grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-2">
            {reviews.map((r, i) => (
              <motion.figure
                key={r.id}
                data-testid={`review-card-${r.id}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.08 }}
                className="bg-[#0D0D0F] p-8 transition-colors duration-500 hover:bg-[#121215] sm:p-10"
              >
                <Stars n={r.rating} />
                <blockquote className="mt-6 text-base leading-relaxed text-[#F3F3F5] sm:text-lg">
                  "{r.text}"
                </blockquote>
                <figcaption className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8E8E93]">
                  {r.name}
                  {r.project_type ? ` — ${r.project_type}` : ""}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}

        <a
          data-testid="reviews-leave-review-link"
          href="/review"
          className="group mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8E8E93] transition-colors hover:text-[#E63946]"
        >
          WORKED WITH ME? LEAVE A REVIEW
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
        </a>
      </div>
    </section>
  );
};
